import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhenIWakeLanding = () => {
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [language, setLanguage] = useState('en');
  const audioRef = useRef(null);
  const { toast } = useToast();

  // Detect browser language and check if email already provided
  useEffect(() => {
    // Check for language parameter in URL first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    let selectedLang = 'en';
    if (langParam && ['en', 'es', 'pt', 'de', 'fr', 'it'].includes(langParam)) {
      selectedLang = langParam;
    } else {
      // Fallback to browser language
      const browserLang = navigator.language.slice(0, 2);
      const supportedLangs = ['en', 'es', 'pt', 'de', 'fr', 'it'];
      selectedLang = supportedLangs.includes(browserLang) ? browserLang : 'en';
    }
    
    setLanguage(selectedLang);
    console.log('🌍 Language set to:', selectedLang);
    
    // Check if user already provided email in any landing page
    const savedEmail = localStorage.getItem('syntetik_user_email');
    if (savedEmail) {
      setEmailSubmitted(true);
      setEmail(savedEmail);
    }
  }, []);

  const trackInfo = {
    id: '6',
    title: 'When I Wake',
    artist: 'SYNTETIK',
    album: 'SYNTETIK',
    year: '2025',
    duration: '4:12',
    audioUrl: 'https://0xx2mevl0on6cu8y.public.blob.vercel-storage.com/When%20i%20wake%20mp3%20Version%20Publica.mp3',
    artwork: 'https://customer-assets.emergentagent.com/job_synthpop-portal/artifacts/ounkx60o_Untitled%20design.png'
  };

  const messages = {
    en: {
      unlockTitle: "🎵 Stream Full Song",
      unlockDesc: "Enter your email to unlock high-quality streaming + get notified of new SYNTETIK releases",
      buttonText: "🎵 UNLOCK FULL SONG",
      unlockedTitle: "✅ Full Access Unlocked!",
      unlockedDesc: "Enjoy streaming + discover more tracks",
      exploreMore: "🎶 EXPLORE MORE SYNTETIK"
    },
    es: {
      unlockTitle: "🎵 Reproduce la Canción Completa",
      unlockDesc: "Ingresa tu email para desbloquear streaming de alta calidad + recibe notificaciones de nuevos lanzamientos de SYNTETIK",
      buttonText: "🎵 DESBLOQUEAR CANCIÓN",
      unlockedTitle: "✅ ¡Acceso Completo Desbloqueado!",
      unlockedDesc: "Disfruta del streaming + descubre más pistas",
      exploreMore: "🎶 EXPLORAR MÁS SYNTETIK"
    },
    pt: {
      unlockTitle: "🎵 Reproduzir Música Completa",
      unlockDesc: "Digite seu email para desbloquear streaming de alta qualidade + receba notificações dos novos lançamentos SYNTETIK",
      buttonText: "🎵 DESBLOQUEAR MÚSICA",
      unlockedTitle: "✅ Acesso Completo Desbloqueado!",
      unlockedDesc: "Aproveite o streaming + descubra mais faixas",
      exploreMore: "🎶 EXPLORAR MAIS SYNTETIK"
    },
    de: {
      unlockTitle: "🎵 Ganzes Lied Streamen",
      unlockDesc: "Gib deine E-Mail ein, um hochwertiges Streaming freizuschalten + Benachrichtigungen über neue SYNTETIK-Veröffentlichungen erhalten",
      buttonText: "🎵 LIED FREISCHALTEN",
      unlockedTitle: "✅ Vollzugang Freigeschaltet!",
      unlockedDesc: "Genieße das Streaming + entdecke weitere Tracks",
      exploreMore: "🎶 MEHR SYNTETIK ENTDECKEN"
    },
    fr: {
      unlockTitle: "🎵 Écouter la Chanson Complète",
      unlockDesc: "Entrez votre email pour débloquer le streaming haute qualité + recevoir les notifications des nouvelles sorties SYNTETIK",
      buttonText: "🎵 DÉBLOQUER LA CHANSON",
      unlockedTitle: "✅ Accès Complet Débloqué !",
      unlockedDesc: "Profitez du streaming + découvrez plus de morceaux",
      exploreMore: "🎶 EXPLORER PLUS SYNTETIK"
    },
    it: {
      unlockTitle: "🎵 Riproduci Canzone Completa",
      unlockDesc: "Inserisci la tua email per sbloccare lo streaming di alta qualità + ricevere notifiche delle nuove uscite SYNTETIK",
      buttonText: "🎵 SBLOCCA CANZONE",
      unlockedTitle: "✅ Accesso Completo Sbloccato!",
      unlockedDesc: "Goditi lo streaming + scopri più tracce",
      exploreMore: "🎶 ESPLORA PIÙ SYNTETIK"
    }
  };

  // Audio loading optimization
  useEffect(() => {
    if (emailSubmitted && audioRef.current && !audioLoading && !audioError) {
      setAudioLoading(true);
      setAudioError(false);
      
      const audio = audioRef.current;
      audio.preload = 'metadata';
      
      const handleCanPlay = () => {
        setAudioLoading(false);
        console.log('🎵 When I Wake audio ready for streaming');
      };
      
      const handleError = (e) => {
        setAudioLoading(false);
        setAudioError(true);
        console.error('🎵 Audio loading error:', e);
      };
      
      const loadTimeout = setTimeout(() => {
        setAudioLoading(false);
        setAudioError(false); // Allow retry
        console.warn('🎵 Audio loading timeout - ready to try playing');
      }, 8000); // 8 second timeout
      
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('error', handleError);
      audio.load();
      
      return () => {
        clearTimeout(loadTimeout);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('error', handleError);
      };
    }
  }, [emailSubmitted]); // Removed audioLoading from dependencies

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/email-track-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.trim(),
          track_name: trackInfo.title,
          source: `when-i-wake-landing-${language}`,
          language: language
        })
      });

      if (response.ok) {
        setEmailSubmitted(true);
        localStorage.setItem('syntetik_user_email', email.trim());
        
        toast({
          title: "✅ Success!",
          description: "Email saved! Enjoy streaming When I Wake",
        });
      } else {
        // Handle non-200 responses
        const errorData = await response.text();
        console.error('Server error:', errorData);
        toast({
          title: "❌ Server Error",
          description: "Please try again in a moment.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Email submission error:', error);
      toast({
        title: "❌ Connection Error", 
        description: "Please check your internet connection and try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async () => {
    if (!audioRef.current) return;
    
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Reset any errors and try to play
        setAudioError(false);
        await audioRef.current.play();
        setIsPlaying(true);
        
        // Track streaming event
        try {
          await fetch(`${API}/tracks/${trackInfo.id}/play`, { method: 'POST' });
        } catch (trackError) {
          console.error('Failed to track play:', trackError);
          // Don't block playback for tracking errors
        }
      }
    } catch (error) {
      console.error('Audio play error:', error);
      setAudioError(true);
      setIsPlaying(false);
      
      // Try to reload audio source
      if (audioRef.current) {
        audioRef.current.load();
      }
      
      toast({
        title: "Audio Error",
        description: "Audio loading failed. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleExploreMore = () => {
    window.location.href = '/#music';
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background with your artwork */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${trackInfo.artwork})`,
          filter: 'blur(20px) brightness(0.3)',
          transform: 'scale(1.1)'
        }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
      
      {/* Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-lg bg-black/80 backdrop-blur-md border-white/10 p-8">
          
          {/* Track artwork and info */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <img 
                src={trackInfo.artwork}
                alt={trackInfo.title}
                className="w-48 h-48 mx-auto rounded-lg shadow-2xl object-cover"
              />
            </div>
            
            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              {trackInfo.title}
            </h1>
            <p className="text-xl text-gray-300 mb-2">{trackInfo.artist}</p>
            <p className="text-sm text-gray-400">{trackInfo.album} • {trackInfo.year} • {trackInfo.duration}</p>
          </div>

          {!emailSubmitted ? (
            /* Email capture form */
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-3 text-blue-300">
                  {messages[language].unlockTitle}
                </h2>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {messages[language].unlockDesc}
                </p>
              </div>
              
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full p-4 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all"
                  required
                />
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white font-semibold py-4 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "..." : messages[language].buttonText}
                </Button>
              </form>
            </div>
          ) : (
            /* Audio player */
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-semibold mb-3 text-green-400">
                  {messages[language].unlockedTitle}
                </h2>
                <p className="text-gray-300 text-sm">
                  {messages[language].unlockedDesc}
                </p>
              </div>

              {/* Audio element with optimization */}
              <audio
                ref={audioRef}
                preload="metadata"
                onEnded={() => setIsPlaying(false)}
                onError={() => setAudioError(true)}
              >
                <source src={trackInfo.audioUrl} type="audio/mpeg" />
              </audio>

              {/* Play/Pause Button */}
              <div className="flex justify-center">
                <Button
                  onClick={handlePlay}
                  disabled={audioLoading || audioError}
                  className={`w-20 h-20 rounded-full ${
                    audioLoading ? 'bg-yellow-600/20 border-yellow-400/50' :
                    audioError ? 'bg-red-600/20 border-red-400/50' :
                    'bg-blue-600/20 border-blue-400/50 hover:bg-blue-600/30'
                  } border-2 flex items-center justify-center transition-all transform hover:scale-105`}
                >
                  {audioLoading ? (
                    <span className="text-2xl">⏳</span>
                  ) : audioError ? (
                    <span className="text-2xl">❌</span>
                  ) : isPlaying ? (
                    <span className="text-2xl">⏸️</span>
                  ) : (
                    <span className="text-2xl ml-1">▶️</span>
                  )}
                </Button>
              </div>

              {/* Status text */}
              <div className="text-center text-sm text-gray-400">
                {audioLoading && "Loading audio..."}
                {audioError && "Audio unavailable"}
                {!audioLoading && !audioError && (isPlaying ? "Now playing..." : "Ready to play")}
              </div>

              {/* Explore more button */}
              <Button
                onClick={handleExploreMore}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-4 rounded-lg transition-all transform hover:scale-105"
              >
                {messages[language].exploreMore}
              </Button>
            </div>
          )}
          
          {/* SYNTETIK branding */}
          <div className="text-center mt-8 pt-6 border-t border-white/10">
            <p className="text-xs text-gray-500 tracking-widest">SYNTETIK WORLD</p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default WhenIWakeLanding;